/**
 * MatchOutreachPanel — fires the introduction email for a Match and
 * surfaces the outreach state (recipient, sent_at, template, status).
 *
 * Two flavors keyed on recipient_role:
 *   - investor — startup pitched to the VC
 *   - founder  — investor pitched to the startup
 *
 * Both include a Cal.com booking CTA when CAL_COM_BOOKING_URL is
 * configured server-side (template renders the link conditionally).
 *
 * Eligibility (enforced server-side, surfaced here):
 *   - Match status must be REVIEWED or PURSUING
 *   - Not already sent (panel shows confirmation card instead of button)
 *   - Recipient must have an email on file (override input available)
 */
import React, { useState } from 'react';
import { Send, CheckCircle2, Mail, Loader2, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import matchService from '../services/matchService';
import { Match, OutreachRecipientRole } from '../types/match';

interface Props {
  match: Match;
  onChanged: () => Promise<void> | void;
}

const ELIGIBLE_STATUSES = new Set(['reviewed', 'pursuing']);

const formatTimestamp = (iso: string | null | undefined): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const MatchOutreachPanel: React.FC<Props> = ({ match, onChanged }) => {
  const [role, setRole] = useState<OutreachRecipientRole>('investor');
  const [overrideEmail, setOverrideEmail] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sending, setSending] = useState(false);

  const alreadySent = !!match.outreach_sent_at;
  const eligible = ELIGIBLE_STATUSES.has(match.status);

  const defaultRecipient =
    role === 'founder'
      ? match.startup?.name
        ? `${match.startup.name} (founder email)`
        : 'startup founder'
      : match.investor?.full_name
        ? `${match.investor.full_name} (investor email)`
        : 'investor';

  const handleSend = async () => {
    setSending(true);
    const body: { recipient_role: OutreachRecipientRole; override_recipient_email?: string } = {
      recipient_role: role,
    };
    if (overrideEmail.trim()) {
      body.override_recipient_email = overrideEmail.trim();
    }
    const result = await matchService.sendMatchOutreach(match.id, body);
    setSending(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    if (result.data?.sent) {
      toast.success(
        `Introduction sent to ${result.data.recipient || 'recipient'} → status: introduced`,
      );
      await onChanged();
    } else {
      toast.error(result.data?.reason || 'Outreach not sent');
    }
  };

  // ---- Already-sent confirmation card ----
  if (alreadySent) {
    return (
      <section className="bg-white border border-green-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">
              Introduction sent
            </h3>
            <p className="text-sm text-gray-700 mt-1">
              Sent to{' '}
              <span className="font-medium">
                {match.outreach_recipient_email || 'recipient'}
              </span>{' '}
              on {formatTimestamp(match.outreach_sent_at)}.
            </p>
            {match.outreach_template && (
              <p className="text-xs text-gray-500 mt-1">
                Template: <code>{match.outreach_template}</code>
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Match auto-advanced to <strong>introduced</strong>. Re-sending requires
              clearing <code>outreach_sent_at</code> on the match row.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // ---- Send-introduction form ----
  return (
    <section className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-start gap-3 mb-4">
        <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">
            Send introduction
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Fires a templated introduction email with the Cal.com booking
            CTA. Eligible once the match is reviewed or pursuing.
          </p>
        </div>
      </div>

      {!eligible && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-xs text-amber-800">
          Match status is <strong>{match.status}</strong>. Advance to{' '}
          <em>reviewed</em> or <em>pursuing</em> before sending.
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            Recipient role
          </label>
          <div className="flex gap-2">
            {(['investor', 'founder'] as OutreachRecipientRole[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRole(r)}
                disabled={sending}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  role === r
                    ? 'border-blue-600 bg-blue-50 text-blue-700 font-medium'
                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                {r === 'investor'
                  ? 'Investor (pitch the startup)'
                  : 'Founder (pitch the investor)'}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            Will go to: <span className="font-medium">{defaultRecipient}</span>
            {overrideEmail.trim() ? ` → overridden: ${overrideEmail.trim()}` : ''}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowAdvanced((s) => !s)}
          className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
        >
          <ChevronDown
            className={`w-3 h-3 transition-transform ${
              showAdvanced ? 'rotate-180' : ''
            }`}
          />
          Advanced (override recipient)
        </button>

        {showAdvanced && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Override recipient email
            </label>
            <input
              type="email"
              value={overrideEmail}
              onChange={(e) => setOverrideEmail(e.target.value)}
              placeholder="ops+preview@muleworks.co"
              disabled={sending}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to use the role-default recipient. Useful for
              previewing to yourself before going live.
            </p>
          </div>
        )}

        <div className="pt-2">
          <button
            type="button"
            onClick={handleSend}
            disabled={!eligible || sending}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {sending ? 'Sending...' : 'Send introduction'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default MatchOutreachPanel;
